import { type ReactNode, createContext, useContext, useMemo } from 'react';
import { type SessionUserDto, nowSlot as toNowSlot } from '@betreuung/shared';
import { usePersons } from '@/api/queries';
import { type PersonLookup, usePersonLookup } from '@/lib/persons';
import { useNow } from './useNow';

/**
 * Data every tab needs: who is signed in, the person list (for colours and
 * names) and a clock that ticks once a minute. Passing these through props would
 * mean threading them through every calendar cell.
 */
interface AppData {
  user: SessionUserDto;
  /**
   * Whether this account may change the calendar at all. Derived once here so
   * no component has to know that "may write" happens to mean "is a
   * Betreuungsperson"; the server enforces the same rule independently.
   */
  canWrite: boolean;
  persons: PersonLookup;
  personsPending: boolean;
  now: Date;
  /** Current 30-minute slot, recomputed on each tick. */
  nowSlot: number;
}

const AppDataContext = createContext<AppData | null>(null);

export function AppDataProvider({
  user,
  children,
}: {
  user: SessionUserDto;
  children: ReactNode;
}) {
  const personsQuery = usePersons();
  const persons = usePersonLookup(personsQuery.data);
  const now = useNow();

  const value = useMemo<AppData>(
    () => ({
      user,
      canWrite: user.isCaregiver,
      persons,
      personsPending: personsQuery.isPending,
      now,
      nowSlot: toNowSlot(now),
    }),
    [user, persons, personsQuery.isPending, now],
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData(): AppData {
  const context = useContext(AppDataContext);
  if (!context) throw new Error('useAppData must be used inside AppDataProvider');
  return context;
}
